import { useEffect, useState } from 'react';
import { clamp } from 'utils/helpers';

export type ProgressBarTone = 'azul' | 'rosa' | 'roxo';

const FILL_CLASS_BY_TONE: Record<ProgressBarTone, string> = {
  azul: 'bg-brand-blue',
  rosa: 'bg-brand-pink',
  roxo: 'bg-brand-purple',
};

interface ProgressBarProps {
  /** Progresso de 0 a 100. Valores fora da faixa são limitados. */
  value: number;
  /**
   * Cor do preenchimento. Padrão `azul`.
   *
   * Com `rosa`, o preenchimento dá só 2,15:1 contra o trilho (WCAG 1.4.11):
   * a tela SHALL mostrar o valor em texto ao lado da barra.
   */
  tone?: ProgressBarTone;
  /** Nome acessível da barra (vira `aria-label`). */
  label: string;
  className?: string;
}

/**
 * Barra de progresso da nova identidade.
 *
 * A largura monta em 0 e anima até o valor no frame seguinte (e do valor
 * anterior ao novo quando `value` muda). Com `prefers-reduced-motion`, não há
 * transição. Para a barra de ciclo por datas, use `cycle-progress-bar`.
 */
export default function ProgressBar({ value, tone = 'azul', label, className = '' }: ProgressBarProps) {
  const progress = Math.round(clamp(value, 0, 100));
  const [renderedProgress, setRenderedProgress] = useState(0);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setRenderedProgress(progress));
    return () => cancelAnimationFrame(frame);
  }, [progress]);

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
      className={`h-2 w-full overflow-hidden rounded-jj-pill bg-surface-200 ${className}`}
    >
      <div
        className={`h-full rounded-jj-pill ${FILL_CLASS_BY_TONE[tone]} transition-[width] duration-jj-slow ease-jj-standard motion-reduce:transition-none`}
        style={{ width: `${renderedProgress}%` }}
      />
    </div>
  );
}
